import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { formatApiError } from "../utils/formatApiError";
import { tableDisplayName } from "../utils/tableDisplay";
import { MapPin, Clock, Users, ArrowLeft, IndianRupee, Table2, X, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollableTable from "../components/ScrollableTable";
import SEO from "../components/SEO";
import RequirePhone from "../components/RequirePhone";
import { useAuth, hasAdminRole } from "@/context/AuthContext";
import { eventToJsonLd } from "../utils/eventJsonLd";
import { eventDisplayTitle, formatEventDate } from "../utils/formatEventDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format payment timing array to readable string
const formatPaymentTiming = (timing) => {
  if (!timing || timing.length === 0) return null;
  const labels = { at_venue: "at venue", in_advance: "in advance" };
  return timing.map(t => labels[t] || t).join(" or ");
};

// Format time from 24h to 12h (e.g., "16:00" -> "4:00 PM")
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  if (/am|pm/i.test(timeStr)) return timeStr;
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${suffix}`;
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [droppingOut, setDroppingOut] = useState(null);
  const { user: authUser } = useAuth();
  const isAdmin = hasAdminRole(authUser);
  const [classesExpanded, setClassesExpanded] = useState(false); // Collapsible classes section

  // Hosted-event in-page signup modal (logged-in path only after the
  // unified-auth refactor — logged-out users redirect to /login).
  const [showSignupModal, setShowSignupModal] = useState(false);
  // States: "phone_required" | "form" | "confirmed_loggedin"
  const [signupModalState, setSignupModalState] = useState("form");
  // Server-side phone_required fallback — captured retry closure, replayed
  // after the user saves a phone via the RequirePhone modal.
  const [phoneFallbackRetry, setPhoneFallbackRetry] = useState(null);
  const [signupForm, setSignupForm] = useState({
    mahjong_style: "",
    slot_id: "",
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState("");

  useEffect(() => {
    fetchEventDetails();
    checkAuth();
    // Admin status is read from AuthContext (see hasAdminRole(authUser) above);
    // no per-page localStorage check needed any more.

    // Poll every 30s, but only when the tab is visible
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchEventDetails();
    }, 30000);

    // Fetch immediately when tab becomes visible again
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchEventDetails();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [eventId]);


  const checkAuth = async () => {
    const token = localStorage.getItem("user_token");
    if (token) {
      try {
        const res = await api.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setCurrentUser(res.data);
      } catch (e) {
        setCurrentUser(null);
      }
    }
  };

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem("user_token");
      const response = await api.get(
        `${API}/events/${eventId}/players`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      
      // Check if event is cancelled - redirect to homepage
      if (response.data.event?.status === "cancelled") {
        toast.error("This event has been cancelled");
        navigate("/");
        return;
      }
      
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Event not found");
        navigate("/");
      } else {
        toast.error("Failed to load event details");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDropout = async (playerId) => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      toast.error("Please login first");
      return;
    }

    if (!window.confirm("Are you sure you want to drop out from this event?")) {
      return;
    }

    setDroppingOut(playerId);
    try {
      await api.delete(
        `${API}/events/${eventId}/players/${playerId}/dropout`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("You have dropped out from this event");
      fetchEventDetails();
    } catch (error) {
      toast.error(formatApiError(error, "Failed to drop out"));
    } finally {
      setDroppingOut(null);
    }
  };

  const handleBookingDropout = async (bookingId) => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      toast.error("Please login first");
      return;
    }

    if (!window.confirm("Are you sure you want to cancel your full table booking?")) {
      return;
    }

    setDroppingOut(bookingId);
    try {
      await api.delete(
        `${API}/events/${eventId}/bookings/${bookingId}/dropout`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your booking has been cancelled");
      fetchEventDetails();
    } catch (error) {
      toast.error(formatApiError(error, "Failed to cancel booking"));
    } finally {
      setDroppingOut(null);
    }
  };

  // Keep event URL metadata in the document during fetch so the tab title
  // and OG tags are not left as the homepage defaults from index.html.
  if (loading) {
    return (
      <div>
        <SEO title="Mahjong event" path={`/events/${eventId}`} />
        <div className="loading">Loading event details...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <SEO title="Event not found" path={`/events/${eventId}`} noindex />
        <div className="loading">Event not found</div>
      </div>
    );
  }

  const { event, solo_players, full_bookings, tables, num_tables } = data;
  const isHostedEvent = event.event_type === "guest_host";
  // table_number is stripped from the API response entirely until an admin
  // flips event.tables_revealed on — see backend _caller_can_see_tables().
  const TABLES_HIDDEN_MSG = "Table assignments will be posted closer to the event";

  // Check if current user is already signed up
  const currentUserPlayer = currentUser ? solo_players.find(p => p.user_id === currentUser.id) : null;
  const currentUserBooking = currentUser ? full_bookings.find(b => b.organizer_user_id === currentUser.id) : null;
  const isUserSignedUp = currentUserPlayer || currentUserBooking;

  // Calculate total slots and sold out status
  // For slotted events, total capacity = sum of slot max_players (not num_tables * 4)
  const totalSlots = (event.slots || []).length > 0
    ? event.slots.reduce((sum, s) => sum + (s.max_players || 0), 0)
    : num_tables * 4;
  const fullTablePlayers = full_bookings.length * 4;
  const totalPlayers = solo_players.length + fullTablePlayers;
  const slotsLeft = Math.max(0, totalSlots - totalPlayers);
  // isSoldOut true when capacity is gone OR admin has manually closed
  // registration. Matches HomePage L733 semantics. The CTA chain below
  // checks event.registration_closed before isSoldOut, so the button copy
  // still distinguishes "Registration Closed" from "Sold Out".
  const isSoldOut = slotsLeft === 0 || event.registration_closed;
  const isAlmostFull = !isSoldOut && slotsLeft <= 4;

  // Check if a table has a class player assigned to it
  const isClassTable = (tableNumber) => {
    return solo_players.some(p => p.signup_type === "class" && p.table_number === tableNumber);
  };

  // Get the style of a class table
  const getClassTableStyle = (tableNumber) => {
    const classPlayer = solo_players.find(p => p.signup_type === "class" && p.table_number === tableNumber);
    return classPlayer?.mahjong_style;
  };

  // ── Registration modal: derived event values ─────────────────────────────
  const availableStyles = isHostedEvent
    ? (event.styles_of_play || [])
    : (event.allowed_styles || []);
  const autoStyle = availableStyles.length === 1 ? availableStyles[0] : null;
  const autoSlotId = (event.slots || []).length === 1 ? event.slots[0].slot_id : null;
  const isLoggedInFlow = !!currentUser;

  // ── Registration modal handlers (logged-in hosted-event only) ───────────
  //
  // After the unified-auth refactor, logged-out users redirect to /login and
  // never reach this modal. Logged-in users on managed events navigate to
  // SignupPage. Only the hosted-logged-in path opens the modal.

  const handleSignupSubmit = async () => {
    setSignupError("");
    const hasMultipleSlots = event.slots && event.slots.length > 1;

    if (!autoStyle && !signupForm.mahjong_style) return setSignupError("Please select which style you want to play at this event.");
    if (hasMultipleSlots && !signupForm.slot_id) return setSignupError("Please select a slot.");

    const chosenStyle = autoStyle || signupForm.mahjong_style;
    const skillLevel = (
      currentUser.style_skills?.find(s => s.style === chosenStyle) ||
      currentUser.style_skills?.[0] ||
      {}
    ).skill_level || "Intermediate";

    setSignupLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      await api.post(
        `${API}/events/${eventId}/signup/solo`,
        {
          mahjong_style: chosenStyle,
          skill_level: skillLevel,
          matching_type: "auto",
          slot_id: autoSlotId || signupForm.slot_id || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSignupModalState("confirmed_loggedin");
    } catch (err) {
      if (err.response?.status === 400 && err.response?.headers?.["x-error-code"] === "phone_required") {
        // Phone was cleared server-side between load and submit — open the
        // phone modal and replay the signup once the phone is saved.
        setPhoneFallbackRetry({ retry: () => handleSignupSubmit() });
        setSignupModalState("phone_required");
      } else {
        setSignupError(formatApiError(err, "Something went wrong. Please try again."));
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const handleSignupModalClose = () => {
    setShowSignupModal(false);
    setSignupModalState("form");
    setSignupError("");
    setSignupForm({ mahjong_style: "", slot_id: "" });
  };

  // ─────────────────────────────────────────────────────────────────────────

  const seoTitle = data?.event
    ? eventDisplayTitle(data.event)
    : "Mahjong event";

  const seoDescription = (() => {
    if (!data?.event) return "";
    const e = data.event;
    if (e.event_type === "guest_host") {
      if (e.tagline) return e.tagline;
      if (e.custom_message) return e.custom_message.substring(0, 150);
      return e.city ? `Hosted Mahjong event in ${e.city}.` : "Hosted Mahjong event on PlayMahjong.in.";
    }
    const name = e.event_name || e.venue_name || "Mahjong event";
    const when = formatEventDate(e.date);
    const where = [e.venue_name, e.address].filter(Boolean).join(", ");
    const city = e.city ? ` in ${e.city}` : "";
    if (when && where) return `${name} on ${when} at ${where}. Join a Mahjong game${city}.`;
    if (when) return `${name} on ${when}. Join a Mahjong game${city}.`;
    if (where) return `${name} at ${where}. Join a Mahjong game${city}.`;
    return `${name}. Join a Mahjong game${city} on PlayMahjong.in.`;
  })();

  return (
    <div>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={data?.event?.image_url || undefined}
        path={`/events/${eventId}`}
        noindex={!!data?.event?.is_private}
        jsonLd={eventToJsonLd(data?.event, eventId)}
      />
      <Header />

      {/* Event Header */}
      <div className="container" style={{ paddingTop: "0.75rem", maxWidth: "768px" }}>
        <div style={{ marginBottom: "0.5rem" }}>
          <Link to="/" style={{ color: "#1B4D3E", textDecoration: "none", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            <ArrowLeft size={14} /> Back to Events
          </Link>
        </div>
        
        <h1 style={{ color: "#1B4D3E", fontSize: "1.35rem", fontWeight: "700", marginBottom: "0.15rem" }}>
          {event.event_name || event.venue_name}
        </h1>
        <p style={{ color: "#666", fontSize: "0.9rem", fontWeight: "500" }}>
          {formatEventDate(event.date) || null}
        </p>
        {isAdmin && (
          <Link
            to={`/admin/events/${eventId}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.25rem",
              marginTop: "0.35rem",
              color: "#D32F2F",
              fontSize: "0.78rem",
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            <Pencil size={12} /> Admin: Edit
          </Link>
        )}
      </div>

      <div className="container" style={{ paddingTop: "0.5rem", maxWidth: "768px" }}>
        {/* Event Image */}
        <div style={{
          marginBottom: "1rem",
          borderRadius: "10px",
          overflow: "hidden"
        }}>
          <img
            src={event.image_url && event.image_url !== "None" && event.image_url !== "" ? event.image_url : "/event-placeholder.jpg"}
            alt={event.event_name || event.venue_name}
            fetchPriority="high"
            decoding="async"
            style={{
              width: "100%",
              height: "auto",
              display: "block"
            }}
            onError={(e) => { e.target.onerror = null; e.target.src = '/event-placeholder.jpg'; }}
          />
        </div>

        {/* Event Info Card */}
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
            <div>
              {event.event_name && event.venue_name && (
                <div style={{ fontWeight: 500, marginBottom: 4, fontSize: "0.9rem", color: "#333" }}>
                  {event.venue_name}
                </div>
              )}
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address + (event.city && !event.address.includes(event.city) ? ', ' + event.city : '') + (event.state && !event.address.includes(event.state) ? ', ' + event.state : ''))}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "flex-start", gap: "0.4rem", marginBottom: "0.5rem", textDecoration: "none", color: "inherit" }}
              >
                <MapPin size={18} color="#1B4D3E" style={{ flexShrink: 0, marginTop: "2px" }} />
                <span style={{ fontSize: "0.9rem", textDecoration: "underline", color: "#1B4D3E" }}>
                  {event.address}
                  {event.city && !event.address.includes(event.city) ? `, ${event.city}` : ""}
                  {event.state && !event.address.includes(event.state) ? `, ${event.state}` : ""}
                </span>
              </a>
              {(event.slots || []).length > 0 ? (
                <div style={{ marginBottom: "0.5rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.35rem" }}>
                    <Clock size={18} color="#1B4D3E" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>Time Slots</span>
                  </div>
                  <div style={{ paddingLeft: "1.65rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    {event.slots.map((slot) => {
                      const slotSignups = solo_players.filter(p => p.slot_id === slot.slot_id).length;
                      const spotsLeft = Math.max(0, slot.max_players - slotSignups);
                      const isFull = spotsLeft === 0;
                      return (
                        <div key={slot.slot_id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.9rem" }}>
                          <span>{formatTime(slot.start_time)} – {formatTime(slot.end_time)}</span>
                          <span style={{
                            fontSize: "0.75rem",
                            padding: "0.1rem 0.5rem",
                            borderRadius: "20px",
                            background: isFull ? "#FFEBEE" : "#E8F5E9",
                            color: isFull ? "#C62828" : "#2E7D32",
                            fontWeight: "600"
                          }}>
                            {isFull ? "Full" : `${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem" }}>
                  <Clock size={18} color="#1B4D3E" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: "0.9rem" }}>{formatTime(event.start_time || event.time)}{event.end_time ? ` - ${formatTime(event.end_time)}` : ""}</span>
                </div>
              )}
              {isHostedEvent ? (
                event.price && event.price !== "0" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <IndianRupee size={18} color="#1B4D3E" style={{ flexShrink: 0 }} />
                    <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                      {event.price.startsWith("₹") ? event.price : `₹${event.price}`} per seat
                    </span>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <span style={{ padding: "0.2rem 0.6rem", background: "#E8F5E9", color: "#2E7D32", borderRadius: "20px", fontWeight: "600", fontSize: "0.8rem" }}>
                      Free to Play
                    </span>
                  </div>
                )
              ) : event.cost_per_seat > 0 ? (
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <IndianRupee size={18} color="#1B4D3E" style={{ flexShrink: 0 }} />
                  <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                    ₹{event.cost_per_seat} per seat
                    {event.fee_redeemable_fnb && (
                      <span style={{ fontWeight: "400", fontStyle: "italic" }}> (redeemable against F&B)</span>
                    )}
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <span style={{ padding: "0.2rem 0.6rem", background: "#E8F5E9", color: "#2E7D32", borderRadius: "20px", fontWeight: "600", fontSize: "0.8rem" }}>
                    Free to Play
                  </span>
                </div>
              )}
              {isHostedEvent && event.payment_timing && event.payment_timing.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.25rem" }}>
                  <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>Payment:</span>
                  <span style={{ fontSize: "0.9rem" }}>{formatPaymentTiming(event.payment_timing)}</span>
                </div>
              )}
            </div>

            <div style={{ fontSize: "0.85rem" }}>
              {event.cost_per_seat > 0 && (
                <p style={{ marginBottom: "0.35rem" }}>
                  <strong>Payment:</strong> {event.payment_method === "online" ? "online only" : event.payment_method === "both" ? "online or at venue" : "at venue"}
                  {(event.payment_method === "online" || event.payment_method === "both") && event.payment_link && (
                    <a 
                      href={event.payment_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ marginLeft: "0.5rem", color: "#1B4D3E" }}
                    >
                      (Pay online →)
                    </a>
                  )}
                </p>
              )}
              {event.event_type === "guest_host" ? (
                <div style={{
                  background: "#E8F5E9",
                  borderLeft: "4px solid #1B4D3E",
                  borderRadius: "8px",
                  padding: "1rem",
                  marginBottom: "0.75rem"
                }}>
                  <p style={{
                    fontWeight: "700",
                    color: "#1B4D3E",
                    fontSize: "0.75rem",
                    fontVariant: "small-caps",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "0.25rem",
                    margin: "0 0 0.25rem 0"
                  }}>
                    Hosted by
                  </p>
                  {event.host_display_name && (
                    <p style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1A1A1A", margin: "0 0 0.35rem 0" }}>
                      {event.host_display_name}
                    </p>
                  )}
                  {event.host_contact_email && (
                    <a href={`mailto:${event.host_contact_email}`} style={{ display: "block", fontSize: "0.85rem", color: "#1B4D3E", marginBottom: "0.2rem" }}>
                      {event.host_contact_email}
                    </a>
                  )}
                  {event.host_contact_phone && (
                    <a href={`tel:${event.host_contact_phone}`} style={{ display: "block", fontSize: "0.85rem", color: "#1B4D3E", marginBottom: "0.2rem" }}>
                      {event.host_contact_phone}
                    </a>
                  )}
                  {event.host_payment_instructions && (
                    <p style={{ whiteSpace: "pre-line", fontSize: "0.85rem", color: "#333", marginTop: "0.5rem", marginBottom: 0 }}>
                      {event.host_payment_instructions}
                    </p>
                  )}
                  <p style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.75rem", marginBottom: 0, fontStyle: "italic" }}>
                    For all questions, payments, and cancellations — contact the host directly. PlayMahjong.in is not responsible for this event.
                  </p>
                </div>
              ) : (
                event.host && (
                  <p style={{ marginBottom: "0.35rem" }}>
                    <strong>Host:</strong> {event.host}
                  </p>
                )
              )}
              <div style={{ marginBottom: "0.35rem" }}>
                <strong style={{ display: "block", marginBottom: "0.4rem" }}>Styles:</strong>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                  {(event.event_type === "guest_host" ? (event.styles_of_play || []) : (event.allowed_styles || [])).map((style) => (
                    <span key={style} style={{
                      padding: "0.2rem 0.6rem",
                      background: "#E8F5E9",
                      color: "#1B4D3E",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: "600"
                    }}>
                      {style}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Classes Available Section - Collapsible */}
              {event.classes_available && (
                <div style={{ 
                  marginTop: "1rem", 
                  background: "#F3E5F5", 
                  borderRadius: "8px",
                  borderLeft: "4px solid #9C27B0"
                }}>
                  {/* Clickable Header - Single Line */}
                  <button
                    onClick={() => setClassesExpanded(!classesExpanded)}
                    style={{ 
                      width: "100%",
                      padding: "0.6rem 0.75rem",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                    data-testid="classes-toggle"
                  >
                    <span style={{ fontWeight: "600", color: "#9C27B0", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span>🎓</span> Classes Available
                    </span>
                    {classesExpanded ? <ChevronUp size={20} color="#9C27B0" /> : <ChevronDown size={20} color="#9C27B0" />}
                  </button>
                  
                  {/* Expanded Content */}
                  {classesExpanded && (
                    <div style={{ padding: "0 0.75rem 0.75rem 0.75rem" }}>
                      {/* Multiple Teachers - New Format */}
                      {event.event_teachers?.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                          {event.event_teachers.map((teacher, idx) => (
                            <div 
                              key={teacher.teacher_id || idx}
                              style={{ 
                                padding: "0.5rem 0.75rem", 
                                background: "#FFF",
                                borderRadius: "6px"
                              }}
                            >
                              <p style={{ fontWeight: "600", color: "#7B1FA2", marginBottom: "0.25rem" }}>
                                {teacher.teacher_name}
                              </p>
                              {teacher.teacher_phone && (
                                <p style={{ fontSize: "0.85rem", marginBottom: "0.15rem" }}>
                                  <strong>Contact:</strong>{" "}
                                  <a href={`tel:${teacher.teacher_phone}`} style={{ color: "#9C27B0" }}>
                                    {teacher.teacher_phone}
                                  </a>
                                </p>
                              )}
                              {teacher.styles_for_event?.length > 0 && (
                                <p style={{ fontSize: "0.85rem", marginBottom: "0.15rem" }}>
                                  <strong>Teaching:</strong> {teacher.styles_for_event.join(", ")}
                                </p>
                              )}
                              <p style={{ fontSize: "0.85rem" }}>
                                {teacher.fee > 0 && <><strong>Fee:</strong> ₹{teacher.fee} | </>}
                                <strong>Min:</strong> {teacher.min_signups > 0 ? `${teacher.min_signups} class${teacher.min_signups > 1 ? 'es' : ''}` : "None"}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Legacy Single Teacher Format */
                        <>
                          {event.class_teacher_name && (
                            <p style={{ marginBottom: "0.25rem" }}>
                              <strong>Teacher:</strong> {event.class_teacher_name}
                            </p>
                          )}
                          {event.class_teacher_phone && (
                            <p style={{ marginBottom: "0.25rem" }}>
                              <strong>Contact:</strong> <a href={`tel:${event.class_teacher_phone}`} style={{ color: "#9C27B0" }}>{event.class_teacher_phone}</a>
                            </p>
                          )}
                          {event.class_styles?.length > 0 && (
                            <p style={{ marginBottom: "0.25rem" }}>
                              <strong>Styles taught:</strong> {event.class_styles.join(", ")}
                            </p>
                          )}
                          {event.class_fee > 0 && (
                            <p style={{ marginBottom: "0.25rem" }}>
                              <strong>Class Fee:</strong> ₹{event.class_fee} per class
                            </p>
                          )}
                          <p style={{ marginBottom: "0.5rem", fontSize: "0.85rem" }}>
                            <strong>Minimums:</strong>{" "}
                            {event.class_min_signups > 0 
                              ? `This teacher requires you to sign up for a minimum of ${event.class_min_signups} classes.`
                              : "This teacher does not require any minimum number of classes."
                            }
                          </p>
                        </>
                      )}
                      <p style={{ marginTop: "0.75rem", padding: "0.5rem", background: "#E1BEE7", borderRadius: "6px", fontSize: "0.8rem", color: "#4A148C" }}>
                        Use the signup button and select the class option. All class queries and payments to be coordinated directly with the teacher.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {isSoldOut && (
                <span style={{
                  background: "linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)",
                  color: "#FFF",
                  padding: "0.25rem 0.6rem",
                  borderRadius: "20px",
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  textTransform: "uppercase"
                }}>
                  Sold Out
                </span>
              )}
              {event.status === "locked" && (
                <span style={{
                  background: "linear-gradient(135deg, #616161 0%, #424242 100%)",
                  color: "#FFF",
                  padding: "0.25rem 0.6rem",
                  borderRadius: "20px",
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  textTransform: "uppercase"
                }}>
                  Signups Locked
                </span>
              )}
              {event.status === "cancelled" && (
                <span style={{
                  background: "linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)",
                  color: "#FFF",
                  padding: "0.25rem 0.6rem",
                  borderRadius: "20px",
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  textTransform: "uppercase"
                }}>
                  Cancelled
                </span>
              )}
              {isAlmostFull && !isSoldOut && event.status === "open" && (
                <span style={{
                  background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                  color: "#FFF",
                  padding: "0.25rem 0.6rem",
                  borderRadius: "20px",
                  fontSize: "0.7rem",
                  fontWeight: "700",
                  textTransform: "uppercase"
                }}>
                  Almost Full - {slotsLeft} left
                </span>
              )}
            </div>
          </div>

          {/* About this event — hosted events only */}
          {event.event_type === "guest_host" && event.custom_message && (
            <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid #E2E2DC" }}>
              <h3 style={{ fontSize: "0.95rem", fontWeight: "700", color: "#1B4D3E", marginBottom: "0.5rem" }}>
                About this event
              </h3>
              <p style={{ fontSize: "0.9rem", color: "#333", lineHeight: "1.6", whiteSpace: "pre-line", margin: 0 }}>
                {event.custom_message}
              </p>
            </div>
          )}

          <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid #E2E2DC" }}>
            {isUserSignedUp ? (
              <button 
                onClick={() => {
                  if (currentUserPlayer) {
                    handleDropout(currentUserPlayer.id);
                  } else if (currentUserBooking) {
                    // Handle booking dropout
                    handleBookingDropout(currentUserBooking.id);
                  }
                }}
                className="btn" 
                style={{ 
                  width: "100%", 
                  background: "#FFEBEE", 
                  color: "#D32F2F",
                  border: "1px solid #D32F2F"
                }}
                disabled={droppingOut}
                data-testid="dropout-button"
              >
                {droppingOut ? "Dropping out..." : "Drop Out of Event"}
              </button>
            ) : event.registration_closed ? (
              <button
                className="btn"
                style={{
                  width: "100%",
                  background: "#F5F5F5",
                  color: "#555",
                  cursor: "not-allowed",
                  fontWeight: "600",
                  border: "1px solid #DDD",
                }}
                disabled
                data-testid="registration-closed-button"
              >
                Registration Closed
              </button>
            ) : event.status !== "locked" && event.status !== "cancelled" && !isSoldOut ? (
              (() => {
                // Unified flow: two CTAs site-wide. "Sign up to play" always
                // shown. "Sign up for a class" shown only when classes are
                // available on this event (managed events only — hosted
                // events never set classes_available).
                const startSignup = (action) => {
                  if (!isLoggedInFlow) {
                    navigate(`/login?event=${eventId}&signup=${action}`);
                    return;
                  }
                  // Logged-in: managed events go to SignupPage with the
                  // chosen action as a query param. Hosted events open the
                  // existing in-page modal (hosted never has classes, so
                  // action is always "play" for them).
                  if (!isHostedEvent) {
                    navigate(`/events/${eventId}/signup?signup=${action}`);
                    return;
                  }
                  if (!currentUser.phone) {
                    setSignupModalState("phone_required");
                    setShowSignupModal(true);
                    return;
                  }
                  setSignupForm({ mahjong_style: "", slot_id: "" });
                  setSignupModalState("form");
                  setShowSignupModal(true);
                };
                return (
                  <>
                    <button
                      className="btn btn-primary"
                      style={{ width: "100%" }}
                      data-testid="signup-button"
                      onClick={() => startSignup("play")}
                    >
                      Sign up to play
                    </button>
                    {event.classes_available && (
                      <button
                        className="btn"
                        style={{
                          width: "100%",
                          marginTop: "0.5rem",
                          background: "#FFF",
                          color: "#1B4D3E",
                          border: "1px solid #1B4D3E",
                          fontWeight: "600",
                        }}
                        data-testid="signup-class-button"
                        onClick={() => startSignup("class")}
                      >
                        Sign up for a class
                      </button>
                    )}
                  </>
                );
              })()
            ) : isSoldOut ? (
              <button 
                className="btn" 
                style={{ 
                  width: "100%", 
                  background: "#D32F2F", 
                  color: "#FFF",
                  cursor: "not-allowed",
                  fontWeight: "700"
                }} 
                disabled
                data-testid="sold-out-button"
              >
                Sold Out
              </button>
            ) : (
              <button 
                className="btn" 
                style={{ 
                  width: "100%", 
                  background: "#E0E0E0", 
                  color: "#666",
                  cursor: "not-allowed"
                }} 
                disabled
              >
                {event.status === "cancelled" ? "Event Cancelled" : "Signups Locked"}
              </button>
            )}
          </div>
        </div>

        {/* Player List / Class Signups / Full Table Bookings / Table Overview
            render regardless of registration_closed or capacity — even when
            an event is sold out or registration is closed, attendees and
            visitors should still be able to see who's signed up, who's at
            which table, and the full slate of event details. */}

        {/* Solo Signups (Player List) */}
        <div style={{ marginTop: "1rem", marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#1B4D3E", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Users size={24} />
            Player List
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#666", marginBottom: "1.25rem" }}>
            {isHostedEvent
              ? "These players have signed up to play. The host will assign tables."
              : "These players have signed up to play. Admins will assign tables."}
          </p>

          {(() => {
            const nonClassPlayers = solo_players.filter(p => p.signup_type !== "class");
            if (nonClassPlayers.length === 0) {
              return (
                <div className="card" style={{ textAlign: "center", padding: "2rem", color: "#666660" }}>
                  No solo signups yet
                </div>
              );
            }

            const renderPlayerTable = (players) => (
              <ScrollableTable>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "500px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #E2E2DC" }}>
                      <th style={{ textAlign: "left", padding: "0.75rem", color: "#1B4D3E", whiteSpace: "nowrap" }}>Name</th>
                      <th style={{ textAlign: "left", padding: "0.75rem", color: "#1B4D3E", whiteSpace: "nowrap" }}>Table</th>
                      <th style={{ textAlign: "left", padding: "0.75rem", color: "#1B4D3E", whiteSpace: "nowrap" }}>Style</th>
                      <th style={{ textAlign: "left", padding: "0.75rem", color: "#1B4D3E", whiteSpace: "nowrap" }}>Skill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map((player) => {
                    const isCurrentUser = currentUser && player.user_id === currentUser.id;
                    const isPaid = player.payment_status === "paid";

                    return (
                      <tr key={player.id} style={{ borderBottom: "1px solid #E2E2DC", background: isCurrentUser ? "#F5F7F5" : "transparent" }}>
                        <td style={{ padding: "0.75rem" }}>
                          <div>
                            {player.name}
                            {isCurrentUser && <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "#1B4D3E", fontWeight: "600" }}>(You)</span>}
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {!event.tables_revealed ? (
                            <span style={{ color: "#999", fontSize: "0.85rem", fontStyle: "italic" }}>{TABLES_HIDDEN_MSG}</span>
                          ) : player.table_number ? (
                            <span style={{ fontWeight: "600", color: "#1B4D3E" }}>{tableDisplayName(event, player.table_number)}</span>
                          ) : (
                            <span style={{ color: "#1565C0", fontSize: "0.85rem", fontStyle: "italic" }}>{isHostedEvent ? "Host will assign" : "Admin will assign"}</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          <span style={{ 
                            padding: "0.25rem 0.5rem", 
                            background: ["Taiwanese", "Japanese", "Hong Kong"].includes(player.mahjong_style) ? "#E3F2FD" : 
                                       ["Willingdon", "ATF Western", "American"].includes(player.mahjong_style) ? "#FFF3E0" : 
                                       ["Singapore", "International"].includes(player.mahjong_style) ? "#E8F5E9" : "#F5F5F5", 
                            borderRadius: "4px", 
                            fontSize: "0.85rem" 
                          }}>
                            {player.mahjong_style}
                          </span>
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.9rem" }}>{player.skill_level}</td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </ScrollableTable>
            );

            const eventSlots = event.slots || [];
            if (eventSlots.length === 0) {
              return renderPlayerTable(nonClassPlayers);
            }

            const unassigned = nonClassPlayers.filter(p => !p.slot_id);
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                {eventSlots.map((slot) => {
                  const slotPlayers = nonClassPlayers.filter(p => p.slot_id === slot.slot_id);
                  return (
                    <div key={slot.slot_id}>
                      <div style={{
                        fontWeight: "600", fontSize: "0.95rem", color: "#1B4D3E",
                        marginBottom: "0.5rem", paddingBottom: "0.3rem",
                        borderBottom: "1px solid #E2E2DC"
                      }}>
                        {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                        <span style={{ fontSize: "0.8rem", color: "#888", fontWeight: "400", marginLeft: "0.5rem" }}>
                          ({slotPlayers.length} / {slot.max_players})
                        </span>
                      </div>
                      {slotPlayers.length === 0 ? (
                        <p style={{ color: "#AAA", fontSize: "0.85rem", margin: "0.5rem 0" }}>No signups for this slot yet</p>
                      ) : renderPlayerTable(slotPlayers)}
                    </div>
                  );
                })}
                {unassigned.length > 0 && (
                  <div>
                    <div style={{
                      fontWeight: "600", fontSize: "0.95rem", color: "#888",
                      marginBottom: "0.5rem", paddingBottom: "0.3rem",
                      borderBottom: "1px solid #E2E2DC"
                    }}>Unassigned (no slot)</div>
                    {renderPlayerTable(unassigned)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Class Signups */}
        {solo_players.filter(p => p.signup_type === "class").length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem", color: "#9C27B0", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "1.3rem" }}>🎓</span>
              Class Signups
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#666", marginBottom: "1rem" }}>
              These players have signed up for classes.
            </p>
            <div className="card" style={{ borderLeft: "4px solid #9C27B0" }}>
              {event.class_fee > 0 && (
                <p style={{ marginBottom: "1rem", fontSize: "0.85rem", color: "#7B1FA2" }}>
                  Class Fee: ₹{event.class_fee} (pay teacher directly)
                </p>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #E2E2DC" }}>
                    <th style={{ textAlign: "left", padding: "0.75rem", color: "#9C27B0" }}>Name</th>
                    <th style={{ textAlign: "left", padding: "0.75rem", color: "#9C27B0" }}>Table</th>
                    <th style={{ textAlign: "left", padding: "0.75rem", color: "#9C27B0" }}>Style</th>
                    <th style={{ textAlign: "left", padding: "0.75rem", color: "#9C27B0" }}>Played Before</th>
                  </tr>
                </thead>
                <tbody>
                  {solo_players
                    .filter(p => p.signup_type === "class")
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                    .map((player) => {
                    const isCurrentUser = currentUser && player.user_id === currentUser.id;
                    return (
                      <tr key={player.id} style={{ borderBottom: "1px solid #E2E2DC", background: isCurrentUser ? "#F3E5F5" : "transparent" }}>
                        <td style={{ padding: "0.75rem" }}>
                          {player.name}
                          {isCurrentUser && <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "#9C27B0", fontWeight: "600" }}>(You)</span>}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {!event.tables_revealed ? (
                            <span style={{ color: "#999", fontSize: "0.85rem", fontStyle: "italic" }}>{TABLES_HIDDEN_MSG}</span>
                          ) : player.table_number ? (
                            <span style={{ padding: "0.25rem 0.5rem", background: "#F3E5F5", color: "#7B1FA2", borderRadius: "4px", fontWeight: "600" }}>
                              {tableDisplayName(event, player.table_number)}
                            </span>
                          ) : (
                            <span style={{ color: "#9C27B0", fontSize: "0.85rem", fontStyle: "italic" }}>Admin will assign</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.85rem" }}>{player.mahjong_style}</td>
                        <td style={{ padding: "0.75rem", fontSize: "0.85rem" }}>
                          {player.has_played_before == null
                            ? <span style={{ color: "#AAA" }}>—</span>
                            : (player.has_played_before ? "Yes" : "No")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Full Table Bookings */}
        {full_bookings.length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#1B4D3E", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Table2 size={24} />
              Full Table Bookings
            </h2>
            <div style={{ display: "grid", gap: "1rem" }}>
              {full_bookings.map((booking) => (
                <div key={booking.id} className="card" style={{ borderLeft: "4px solid #1B4D3E" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    {event.tables_revealed ? (
                      <h3 style={{ fontSize: "1.25rem", fontWeight: "700", color: "#1B4D3E" }}>
                        {tableDisplayName(event, booking.table_number)}
                      </h3>
                    ) : (
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "600", fontStyle: "italic", color: "#999" }}>
                        {TABLES_HIDDEN_MSG}
                      </h3>
                    )}
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <span className="badge badge-open">{booking.mahjong_style}</span>
                      {solo_players.some(p => p.signup_type === "class" && p.table_number === booking.table_number) && (
                        <span style={{ background: "#9C27B0", color: "#fff", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600" }}>
                          🎓 Class
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={{ marginBottom: "0.5rem", color: "#666660" }}>
                    <strong>Skill Level:</strong> {booking.skill_level}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.5rem", marginTop: "0.75rem" }}>
                    {booking.players.map((player, idx) => (
                      <div key={idx} style={{ padding: "0.5rem 0.75rem", background: "#F5F7F5", borderRadius: "6px" }}>
                        {player.name}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table Overview */}
        <div style={{ marginBottom: "2rem" }}>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem", color: "#1B4D3E" }}>Table Overview</h2>
          <p style={{ fontSize: "0.875rem", color: "#666", marginBottom: "1rem" }}>
            These are the current confirmed table assignments.
          </p>
          {!event.tables_revealed ? (
            <div className="card" style={{ textAlign: "center", padding: "2rem", color: "#999", fontStyle: "italic" }}>
              {TABLES_HIDDEN_MSG}
            </div>
          ) : (() => {
            const renderTableGrid = (filteredSoloPlayers, filteredFullBookings, tableCount = num_tables) => (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
                {Array.from({ length: tableCount }, (_, i) => i + 1).map(tableNum => {
                  const fullBooking = filteredFullBookings.find(b => b.table_number === tableNum);
                  const tablePlayers = filteredSoloPlayers.filter(p => p.table_number === tableNum);
              const playerCount = fullBooking ? 4 : tablePlayers.length;
              const isFull = playerCount >= 4;
              
              // Check if this is a class table
              const classTables = event.class_tables || [];
              const classTableInfo = classTables.find(ct => ct.table_number === tableNum);
              const isClassTable = !!classTableInfo;
              
              // Get table style and skill from first player or full booking
              const tableStyle = fullBooking?.mahjong_style || (tablePlayers.length > 0 ? tablePlayers[0].mahjong_style : null);
              const tableSkill = fullBooking?.skill_level || (tablePlayers.length > 0 ? tablePlayers[0].skill_level : null);
              
              // Build slots array - 4 slots per table
              const slots = [1, 2, 3, 4].map(slotNum => {
                if (fullBooking && fullBooking.players && fullBooking.players[slotNum - 1]) {
                  return fullBooking.players[slotNum - 1].name;
                } else if (tablePlayers[slotNum - 1]) {
                  return tablePlayers[slotNum - 1].name;
                }
                return null; // Empty slot
              });
              
              return (
                <div
                  key={tableNum}
                  className="card"
                  style={{
                    padding: "1rem",
                    borderLeft: `4px solid ${isClassTable ? "#9C27B0" : isFull ? "#2E7D32" : fullBooking || tablePlayers.length > 0 ? "#1B4D3E" : "#E2E2DC"}`,
                    background: isClassTable ? "#F9F5FA" : "white"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <strong style={{ fontSize: "1rem" }}>{tableDisplayName(event, tableNum)}</strong>
                      {isClassTable && (
                        <span style={{ 
                          background: "#9C27B0", 
                          color: "#fff", 
                          padding: "0.15rem 0.4rem", 
                          borderRadius: "4px", 
                          fontSize: "0.6rem", 
                          fontWeight: "700"
                        }}>
                          🎓 CLASS
                        </span>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: "0.75rem", 
                      padding: "0.2rem 0.5rem",
                      borderRadius: "12px",
                      background: isFull ? "#E8F5E9" : playerCount > 0 ? "#FFF3E0" : "#F5F5F5",
                      color: isFull ? "#2E7D32" : playerCount > 0 ? "#E65100" : "#999",
                      fontWeight: "600"
                    }}>
                      {playerCount}/4
                    </span>
                  </div>
                  
                  {/* Class table teaching info */}
                  {isClassTable && classTableInfo && (
                    <div style={{
                      padding: "0.4rem 0.5rem",
                      background: "#E1BEE7",
                      borderRadius: "6px",
                      marginBottom: "0.5rem",
                      fontSize: "0.7rem",
                      color: "#7B1FA2"
                    }}>
                      <div style={{ fontWeight: "600" }}>🎓 {classTableInfo.teacher_name}</div>
                      {classTableInfo.styles && classTableInfo.styles.length > 0 && (
                        <div style={{ fontSize: "0.65rem", marginTop: "0.15rem" }}>
                          Teaching: {classTableInfo.styles.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show style and skill label when table has players */}
                  {!isClassTable && (fullBooking || tablePlayers.length > 0) && tableStyle && (
                    <div style={{ 
                      display: "flex", 
                      gap: "0.5rem", 
                      marginBottom: "0.75rem",
                      flexWrap: "wrap"
                    }}>
                      <span style={{ 
                        fontSize: "0.625rem", 
                        padding: "0.15rem 0.4rem", 
                        background: "#E3F2FD", 
                        color: "#1565C0", 
                        borderRadius: "4px",
                        fontWeight: "600"
                      }}>
                        {tableStyle}
                      </span>
                      {tableSkill && (
                        <span style={{ 
                          fontSize: "0.625rem", 
                          padding: "0.15rem 0.4rem", 
                          background: "#FFF3E0", 
                          color: "#E65100", 
                          borderRadius: "4px",
                          fontWeight: "600"
                        }}>
                          {tableSkill}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 4 Numbered Slots */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                    {slots.map((playerName, idx) => (
                      <div 
                        key={idx}
                        style={{ 
                          display: "flex", 
                          alignItems: "center",
                          padding: "0.4rem 0.5rem",
                          background: playerName ? "#F5F7F5" : "#FAFAFA",
                          borderRadius: "6px",
                          border: playerName ? "1px solid #E2E2DC" : "1px dashed #E2E2DC"
                        }}
                      >
                        <span style={{ 
                          width: "20px", 
                          height: "20px", 
                          borderRadius: "50%", 
                          background: playerName ? "#1B4D3E" : "#E2E2DC",
                          color: playerName ? "white" : "#999",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: "600",
                          marginRight: "0.5rem",
                          flexShrink: 0
                        }}>
                          {idx + 1}
                        </span>
                        <span style={{ 
                          fontSize: "0.8rem", 
                          color: playerName ? "#333" : "#999",
                          fontStyle: playerName ? "normal" : "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {playerName || "Open"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
                })}
              </div>
            );

            const eventSlots = event.slots || [];
            if (eventSlots.length === 0) {
              return renderTableGrid(solo_players, full_bookings);
            }

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                {eventSlots.map((slot) => (
                  <div key={slot.slot_id}>
                    <div style={{
                      fontWeight: "600", fontSize: "0.95rem", color: "#1B4D3E",
                      marginBottom: "0.75rem", paddingBottom: "0.3rem",
                      borderBottom: "1px solid #E2E2DC"
                    }}>
                      {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                    </div>
                    {renderTableGrid(
                      solo_players.filter(p => p.slot_id === slot.slot_id),
                      full_bookings,
                      slot.num_tables || Math.max(1, Math.round((slot.max_players || 4) / 4))
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
      <Footer />

      {/* ── Phone gate (logged-in hosted events, missing phone) ────────── */}
      {showSignupModal && signupModalState === "phone_required" && (
        <RequirePhone
          variant="modal"
          user={currentUser}
          heading="Phone number required"
          reason="The host needs your mobile number to coordinate the event."
          onCancel={handleSignupModalClose}
          onPhoneSaved={async (updatedUser) => {
            setCurrentUser(updatedUser);
            if (phoneFallbackRetry) {
              // Fallback path: replay the signup the backend rejected earlier.
              const retry = phoneFallbackRetry.retry;
              setPhoneFallbackRetry(null);
              try { await retry(); } catch (_) { /* handler owns error state */ }
              return;
            }
            // Proactive path: prefill the form and advance to the form step.
            setSignupForm(f => ({
              ...f,
              first_name: updatedUser.first_name || "",
              last_name: updatedUser.last_name || "",
              email: updatedUser.email || "",
              phone: updatedUser.phone || "",
              style_skills: updatedUser.style_skills?.length
                ? updatedUser.style_skills
                : [{ style: "", skill_level: "" }],
            }));
            setSignupModalState("form");
          }}
        />
      )}

      {/* ── Registration Modal ─────────────────────────────────────────── */}
      {showSignupModal && signupModalState !== "phone_required" && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "1rem"
        }}>
          <div style={{
            background: "white", borderRadius: "12px", padding: "1.5rem",
            maxWidth: "480px", width: "100%", maxHeight: "90vh", overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }}>

            {/* STATE: form (logged-in hosted-event signup) */}
            {signupModalState === "form" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem" }}>
                  <h3 style={{ color: "#1B4D3E", margin: 0, fontSize: "1.1rem", fontWeight: "700", paddingRight: "1rem" }}>
                    Sign Up for {event.event_name || event.venue_name}
                  </h3>
                  <button onClick={handleSignupModalClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.4rem", color: "#666", padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>

                <p style={{ fontSize: "0.85rem", color: "#555", marginBottom: "1.25rem", background: "#F5F5F5", borderRadius: "8px", padding: "0.6rem 0.75rem" }}>
                  Registering as <strong>{currentUser.first_name} {currentUser.last_name}</strong> ({currentUser.email})
                </p>

                <p style={{ fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "#1B4D3E", marginBottom: "0.75rem", marginTop: 0 }}>For This Event</p>

                {autoStyle ? (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <label style={{ display: "block", fontSize: "0.8rem", color: "#555", marginBottom: "0.25rem" }}>Style being played</label>
                    <span style={{ padding: "0.3rem 0.75rem", background: "#E8F5E9", color: "#1B4D3E", borderRadius: "20px", fontWeight: "600", fontSize: "0.85rem", display: "inline-block" }}>{autoStyle}</span>
                  </div>
                ) : (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <label style={{ display: "block", fontSize: "0.8rem", color: "#555", marginBottom: "0.25rem" }}>Which style will you play? *</label>
                    <select
                      value={signupForm.mahjong_style}
                      onChange={e => setSignupForm(f => ({ ...f, mahjong_style: e.target.value }))}
                      style={{ width: "100%", padding: "0.5rem 0.6rem", border: "1px solid #DDD", borderRadius: "6px", fontSize: "0.9rem" }}
                    >
                      <option value="">Select style...</option>
                      {availableStyles.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}

                {(event.slots || []).length > 1 && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <label style={{ display: "block", fontSize: "0.8rem", color: "#555", marginBottom: "0.25rem" }}>Which time slot? *</label>
                    <select
                      value={signupForm.slot_id}
                      onChange={e => setSignupForm(f => ({ ...f, slot_id: e.target.value }))}
                      style={{ width: "100%", padding: "0.5rem 0.6rem", border: "1px solid #DDD", borderRadius: "6px", fontSize: "0.9rem" }}
                    >
                      <option value="">Select a slot...</option>
                      {event.slots.map(slot => {
                        const slotSignups = solo_players.filter(p => p.slot_id === slot.slot_id).length;
                        const spotsLeft = Math.max(0, slot.max_players - slotSignups);
                        const isFull = spotsLeft === 0;
                        return (
                          <option key={slot.slot_id} value={slot.slot_id} disabled={isFull}>
                            {formatTime(slot.start_time)} – {formatTime(slot.end_time)}{isFull ? " (Full)" : ` — ${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                <button
                  onClick={handleSignupSubmit}
                  className="btn btn-primary"
                  style={{ width: "100%", marginTop: "1rem" }}
                  disabled={signupLoading}
                >
                  {signupLoading ? "Registering..." : "Confirm Registration"}
                </button>

                {signupError && (
                  <p style={{ color: "#D32F2F", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>{signupError}</p>
                )}
              </>
            )}

            {/* STATE: confirmed_loggedin */}
            {signupModalState === "confirmed_loggedin" && (
              <>
                <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🎉</div>
                  <h3 style={{ color: "#1B4D3E", margin: 0, fontSize: "1.1rem", fontWeight: "700" }}>You're registered!</h3>
                </div>
                <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1.5rem", textAlign: "center", lineHeight: 1.5 }}>
                  You've been signed up for <strong>{event.event_name || event.venue_name}</strong>. See you there!
                </p>
                <button onClick={handleSignupModalClose} className="btn btn-primary" style={{ width: "100%" }}>Done</button>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default EventDetailPage;
